/**
 * World 7 — "Ash Fields" (Fire, levels 61–70). ACT II, chapter 2: the chase
 * after the First Keeper begins; the soul Clan Z caged and broke still burns.
 * Levels/caps stamped from World 7's band (61→70). Fire pool (Hotpaw/Drago —
 * Talion is Clan Z's alone), with at most one outsider (Pikoto) per trainer line-up. Finale boss (slot 0) is Hotpaw — Soot, the Ash Guardian — who grants
 * the first of four shards of the FIRST SOUL (the First Keeper's own),
 * alongside its own Soul Shard (soulSpeciesId: HOTPAW).
 */

import {
  node,
  giftNode,
  giftTracks,
  giftXp,
  rival,
  CAMPAIGN_XP_NORMAL,
  CAMPAIGN_XP_MINI_BOSS,
  CAMPAIGN_XP_ZONE_BOSS,
  HOTPAW,
  TALION,
  DRAGO,
  VOLTARYX,
  ZARICAT,
  TORO,
  PEFAREON,
  PIKOTO,
  type CampaignWorld,
} from './builders.js';
import { GIFT_SKILL_SUIKA } from '../cardShopConfig.js';
import {
  WORLD7_NODE_STORIES as S,
  WORLD7_ZONE_STORIES as Z,
  WORLD7_TAGLINE,
} from './stories/world7.js';

export const WORLD_7: CampaignWorld = {
  worldId: 'w7',
  name: 'Ash Fields',
  element: 'Fire',
  featuredSpeciesId: HOTPAW,
  story: WORLD7_TAGLINE,
  locked: true,
  zones: [
    {
      zoneId: 'z18',
      name: 'Smoke Trail',
      element: 'Fire',
      story: Z['z18'],
      nodes: [
        node({
          nodeId: 'z18-n1',
          kind: 'mini_boss',
          rivalName: 'Zeck the Boss',
          rivalTitle: 'Clan Z Boss',
          ...S['z18-n1'],
          squad: [rival(VOLTARYX, 'Storm'), rival(DRAGO, 'Kiln'), rival(PIKOTO, 'Blaze')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_MINI_BOSS,
          portraitSpeciesId: VOLTARYX,
          soulSpeciesId: VOLTARYX,
        }),
        giftNode({
          nodeId: 'z18-g1',
          giver: 'Ash Sifter',
          label: 'Trailside Gift',
          story: 'Someone sifting the warm ash looks up at you.',
          line: 'Warm ash, warm shards! Take these Soul Shards.',
          reward: { tracks: giftTracks(HOTPAW) },
          portraitSpeciesId: HOTPAW,
        }),
        node({
          nodeId: 'z18-n2',
          kind: 'normal',
          rivalName: 'Timmy',
          rivalTitle: 'Youngster',
          ...S['z18-n2'],
          squad: [rival(DRAGO, 'Scout'), rival(HOTPAW, 'Stride'), rival(PIKOTO, 'Pace')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: DRAGO,
        }),
        node({
          nodeId: 'z18-n3',
          kind: 'normal',
          rivalName: 'Lulu',
          rivalTitle: 'Lass',
          ...S['z18-n3'],
          squad: [rival(PIKOTO, 'Mercy'), rival(HOTPAW, 'Solace'), rival(DRAGO, 'Pardon')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: PIKOTO,
        }),
        node({
          nodeId: 'z18-nw1',
          kind: 'normal',
          encounterKind: 'wild',
          rivalName: 'Zaricat',
          rivalTitle: 'Wild Suikamon',
          ...S['z18-nw1'],
          squad: [rival(ZARICAT, 'Emberclaw'), rival(TORO, 'Cinder'), rival(HOTPAW, 'Scorch')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: ZARICAT,
        }),
        node({
          nodeId: 'z18-n4',
          kind: 'zone_boss',
          rivalName: 'Smoke Gate',
          rivalTitle: 'Locked Door',
          ...S['z18-n4'],
          squad: [rival(DRAGO, 'Threshold'), rival(HOTPAW, 'Lintel'), rival(PIKOTO, 'Hinge')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_ZONE_BOSS,
          portraitSpeciesId: DRAGO,
        }),
      ],
    },
    {
      zoneId: 'z19',
      name: 'Burning Fields',
      element: 'Fire',
      story: Z['z19'],
      nodes: [
        node({
          nodeId: 'z19-n1',
          kind: 'normal',
          rivalName: 'Cliff',
          rivalTitle: 'Bird Keeper',
          ...S['z19-n1'],
          squad: [rival(HOTPAW, 'Watchfire'), rival(PIKOTO, 'Chaff'), rival(DRAGO, 'Stubble')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: HOTPAW,
        }),
        giftNode({
          nodeId: 'z19-g1',
          giver: 'Flame Tender',
          label: 'Training Charm',
          story: 'Beside the trail, a small hand fans a little flame and waves you over.',
          line: 'This charm feeds a young fire. Take it.',
          reward: { xp: giftXp(6) },
          portraitSpeciesId: HOTPAW,
        }),
        node({
          nodeId: 'z19-n2',
          kind: 'normal',
          rivalName: 'Mimi',
          rivalTitle: 'Lass',
          ...S['z19-n2'],
          squad: [rival(PIKOTO, 'Scribble'), rival(DRAGO, 'Inkfire'), rival(HOTPAW, 'Page')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: PIKOTO,
        }),
        node({
          nodeId: 'z19-n3',
          kind: 'normal',
          rivalName: 'Vera',
          rivalTitle: 'Channeler',
          ...S['z19-n3'],
          squad: [rival(DRAGO, 'Vow'), rival(HOTPAW, 'Seal'), rival(PIKOTO, 'Word')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: DRAGO,
        }),
        node({
          nodeId: 'z19-n5',
          kind: 'normal',
          rivalName: 'Zip',
          rivalTitle: 'Clan Z',
          ...S['z19-n5'],
          squad: [rival(TALION, 'Dash'), rival(HOTPAW, 'Ember'), rival(DRAGO, 'Hearth')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: TALION,
        }),
        node({
          nodeId: 'z19-n4',
          kind: 'zone_boss',
          rivalName: 'Fire Choir',
          rivalTitle: 'Wrong Song',
          ...S['z19-n4'],
          squad: [rival(HOTPAW, 'Treble'), rival(DRAGO, 'Descant'), rival(PIKOTO, 'Refrain')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_ZONE_BOSS,
          portraitSpeciesId: HOTPAW,
        }),
      ],
    },
    {
      zoneId: 'z20',
      name: 'Ash Temple',
      element: 'Fire',
      story: Z['z20'],
      nodes: [
        node({
          nodeId: 'z20-n1',
          kind: 'normal',
          rivalName: 'Aggie',
          rivalTitle: 'Medium',
          ...S['z20-n1'],
          squad: [rival(DRAGO, 'Warmth'), rival(HOTPAW, 'Tinder'), rival(PIKOTO, 'Kindle')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: DRAGO,
        }),
        giftNode({
          nodeId: 'z20-g1',
          giver: 'Shrine Keeper',
          label: 'Soul Token',
          story: 'An ember shrine smolders quietly beside your path.',
          line: 'The Ash Fields leave a big handful of Soul Shards here. Take them!',
          reward: { tracks: giftTracks(HOTPAW, true), suika: GIFT_SKILL_SUIKA },
          portraitSpeciesId: HOTPAW,
        }),
        node({
          nodeId: 'z20-n2',
          kind: 'normal',
          rivalName: 'Hurt Spirit',
          rivalTitle: 'Lost Spirit',
          ...S['z20-n2'],
          squad: [rival(HOTPAW, 'Plume'), rival(PIKOTO, 'Cinderfall'), rival(DRAGO, 'Smother')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: HOTPAW,
        }),
        node({
          nodeId: 'z20-n5',
          kind: 'normal',
          rivalName: 'Zap',
          rivalTitle: 'Clan Z',
          ...S['z20-n5'],
          squad: [rival(PEFAREON, 'Shade'), rival(DRAGO, 'Char'), rival(HOTPAW, 'Scald')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: PEFAREON,
        }),
        node({
          nodeId: 'z20-nw2',
          kind: 'normal',
          encounterKind: 'wild',
          rivalName: 'Toro',
          rivalTitle: 'Wild Suikamon',
          ...S['z20-nw2'],
          squad: [rival(TORO, 'Blaze'), rival(ZARICAT, 'Searclaw'), rival(DRAGO, 'Ashfall')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: TORO,
        }),
        node({
          nodeId: 'z20-n3',
          kind: 'mini_boss',
          rivalName: 'Last Ember',
          rivalTitle: 'Fading Ember',
          ...S['z20-n3'],
          squad: [rival(DRAGO, 'Flicker'), rival(HOTPAW, 'Glint'), rival(PIKOTO, 'Spark')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_MINI_BOSS,
          portraitSpeciesId: DRAGO,
        }),
        node({
          nodeId: 'z20-n4',
          kind: 'zone_boss',
          rivalName: 'Soot',
          rivalTitle: 'Ash Guardian',
          ...S['z20-n4'],
          squad: [rival(HOTPAW, 'Soot'), rival(DRAGO, 'Emberkin'), rival(PIKOTO, 'Cinderkin')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_ZONE_BOSS,
          portraitSpeciesId: HOTPAW,
          soulSpeciesId: HOTPAW,
        }),
      ],
    },
  ],
};
